import { Logger } from '~/plugins/log'
import limitedEditionProductsQuery from '~/gql/limitedEditionProducts.gql'
import { pdpMappingQuery } from '~/gql/pdp.gql'

const logger = new Logger()
logger.setContext('Product')

const getDefaultState = () => ({
  products: {},
  stock: {},
  lastUpdate: {},
  signatureSeriesIds: {},
  limitedEditionIds: {},
  productImages: [],
  allChoices: {},
  allParts: {},
  swappableParts: {
    vulcans: ['vcb', 'vcn'],
    velans: ['veb', 'ven'],
    airas: ['ab', 'an']
  },
  baseSKUMapping: {
    pacebreakerslrk: 'pacebreakers',
    pacebreakersesk: 'pacebreakers',
    classics: 'classics3',
    classics2: 'classics3',
    classics2lrk: 'classics3',
    classics3lrk: 'classics3',
    renegadeslrk: 'renegades',
    sierraslrk: 'sierras',
    revoltparts: 'revolts',
    snipersparts: 'snipers',
    vanguardsparts: 'vanguards',
    velanslrk: 'velans',
    velansck: 'velans',
    vulcanslrk: 'vulcans',
    vulcansck: 'vulcans',
    airaslrk: 'airas',
    airasck: 'airas'
  },
  pageModeMapping: {
    pacebreakerslrk: 'lenses',
    pacebreakersesk: 'earsocks',
    classics2lrk: 'lenses',
    classics3lrk: 'lenses',
    renegadeslrk: 'lenses',
    sierraslrk: 'lenses',
    revoltparts: 'parts',
    snipersparts: 'parts',
    vanguardsparts: 'parts',
    velanslrk: 'lenses',
    velansck: 'conversionkit',
    vulcanslrk: 'lenses',
    vulcansck: 'conversionkit',
    airaslrk: 'lenses',
    airasck: 'conversionkit'
  },
  iconMapping: {
    classics3: 'sgi-classics2',
    renegades: 'sgi-renegades',
    sierras: 'sgi-sierras',
    revolts: 'sgi-revolts2',
    snipers: 'sgi-snipers',
    vanguards: 'sgi-vanguards',
    'vulcans-ff': 'sgi-vulcans-ff',
    'vulcans-tf': 'sgi-vulcans-tf',
    'velans-ff': 'sgi-velans-ff',
    'velans-tf': 'sgi-velans-tf',
    'airas-bf': 'sgi-velans-ff',
    'airas-zf': 'sgi-velans-tf'
  },
  spacerMapping: {
    classics3: ['1-rg_shadow.png', 0],
    sierras: ['1-rg_shadow.png', 0],
    renegades: ['1-rg_shadow.png', 0],
    classics3lrk: ['1-c3f_outline.png', 1], // 1- replaced with view angle
    sierraslrk: ['1-sf_outline.png', 1], // 1- replaced with view angle
    renegadeslrk: ['1-rgf_outline.png', 1], // 1- replaced with view angle
    revolts: ['Tspacer-rglrk.png', 0],
    revoltparts: ['Tspacer-rglrk.png', 0],
    snipers: ['Tspacer-rglrk.png', 0],
    snipersparts: ['Tspacer-rglrk.png', 0],
    vanguards: ['Tspacer-rglrk.png', 0],
    vanguardsparts: ['Tspacer-rglrk.png', 0],
    vulcans: ['1-rg_shadow.png', 0],
    vulcanslrk: ['1-vcf_outline-tf.png', 1], // 1- replaced with view angle
    vulcansck: ['1-vcb_outline.png', 1], // 1- replaced with view angle
    velans: ['1-rg_shadow.png', 0],
    velanslrk: ['1-vef_outline-tf.png', 1], // 1- replaced with view angle
    velansck: ['1-veb_outline.png', 1], // 1- replaced with view angle
    airas: ['1-rg_shadow.png', 0],
    airaslrk: ['1-af_outline-tf.png', 1], // 1- replaced with view angle
    airasck: ['1-ab_outline.png', 1], // 1- replaced with view angle
    pacebreakerslrk: ['Tspacer-pblrk.png', 1],
    pacebreakersesk: ['Tspacer-pbesk.png', 1]
  },
  limitedEditionParts: {
    sns_fwt21: {
      offset: 17.1,
      sku: 'snipers-snf_whiteM-snle_fireN-sns_fwt21',
      title: 'FWT21',
      buttonImg: 'designer/button-s_fwt21.png',
      extraImages: ['sungod18/limited-edition/fwt-microfibre.png'],
      staticImage: 'sungod18/limited-edition/snipers-le_fwt21.png'
    },
    vs_fwt21: {
      offset: 17.2,
      sku: 'vanguards-vf_whiteM-vle_fireN-vs_fwt21',
      title: 'FWT21',
      buttonImg: 'designer/button-s_fwt21.png',
      extraImages: ['sungod18/limited-edition/fwt-microfibre.png'],
      staticImage: 'sungod18/limited-edition/vanguards-le_fwt21.png'
    },
    sns_pow21: {
      offset: 17.5,
      sku: 'snipers-snf_whiteM-snle_chromeN-sns_pow21',
      title: 'POW21',
      buttonImg: 'designer/button-s_pow21.png',
      extraImages: ['sungod18/limited-edition/pow-microfibre.png']
    },
    vs_pow21: {
      offset: 17.6,
      sku: 'vanguards-vf_whiteM-vle_chromeN-vs_pow21',
      title: 'POW21',
      buttonImg: 'designer/button-s_pow21.png',
      extraImages: ['sungod18/limited-edition/pow-microfibre.png']
    },
    sns_billymorgan: {
      offset: 7.1,
      sku: 'snipers-snf_blackM-snle_chromeN-sns_billymorgan',
      title: 'Billy Morgan',
      isSignatureSeries: true,
      buttonImg: 'designer/button-s_billymorgan.png',
      extraImages: ['sungod18/limited-edition/billymorgan-microfibre.png'],
      staticImage: 'sungod18/limited-edition/snipers-billymorgan.png'
    },
    vs_billymorgan: {
      offset: 7.2,
      sku: 'vanguards-vf_blackM-vle_chromeN-vs_billymorgan',
      title: 'Billy Morgan',
      isSignatureSeries: true,
      buttonImg: 'designer/button-s_billymorgan.png',
      extraImages: ['sungod18/limited-edition/billymorgan-microfibre.png'],
      staticImage: 'sungod18/limited-edition/vanguards-billymorgan.png'
    }
  },
  limitedEditionProductConfig: {},
  pageConfig: {}
})

export const state = getDefaultState

export const mutations = {
  RESET (state) {
    Object.assign(state, getDefaultState())
  },
  SET_PRODUCT_DETAILS (state, { productId, response, lightweight = false }) {
    state.products = {
      ...state.products,
      [productId]: {
        ...(state.products[productId] || {}),
        ...response
      }
    }
    
    if (!lightweight) state.lastUpdate = { ...state.lastUpdate, [productId]: (new Date()).valueOf() }
  },
  BATCH_SET_PRODUCT_DETAILS (state, { products }) {
    Object.entries(products).forEach(
      ([productId, response]) => {
        state.products = {
          ...state.products,
          [productId]: {
            ...(state.products[productId] || {}),
            ...response
          }
        }
      }
    )
  },
  SET_LIMITED_EDITION_IDS (state, limitedEditionIds) {
    state.limitedEditionIds = limitedEditionIds
  },
  SET_SIGNATURE_SERIES_IDS (state, signatureSeriesIds) {
    state.signatureSeriesIds = signatureSeriesIds
  },
  SET_PRODUCT_IMAGES (state, data) {
    state.productImages = data.results
  },
  SET_STOCK (state, { fields, values }) {
    state.stock = Object.fromEntries(values.map((row) => {
      const rowObject = Object.fromEntries(fields.map((field, idx) => [field, row[idx]]))
      return [rowObject.sku, rowObject]
    }))
  },
  SET_ALL_CHOICES (state) {
    const choices = Object.entries(state.products).map(([productId, product]) => {
      const options = (product.product_details_sorted || []).map(
        ([part, details]) => details.options
      ).flat().map(
        (entry) => {
          let [sku, choice] = entry
          if (Array.isArray(choice)) [sku, choice] = choice

          return {
            ...choice,
            sku,
            [`bundle${productId}`]: { optionId: choice.optionId, selectId: choice.selectId }
          }
        }
      )
      return options
    }).flat()

    state.allChoices = choices.reduce((allChoices, choice) => {
      allChoices[choice.sku] = {
        ...(allChoices[choice.sku] || {}),
        ...choice
      }
      return allChoices
    }, {})
  },
  SET_ALL_PARTS (state) {
    state.allParts = Object.fromEntries(Object.values(state.products).map(
      product => product.product_details_sorted
    ).flat())
  },
  SET_LE_PRODUCT_CONFIG (state, result) {
    state.limitedEditionProductConfig = Object.fromEntries(result.map(row => [row.sku, row]))
  },
  SET_PDP_MAPPING (state, result) {
    result.forEach((product) => {
      state.pageModeMapping[product.sku] = product.pageMode
      state.baseSKUMapping[product.sku] = product.baseSku
      state.iconMapping[product.sku] = product.icon
    })
  },
  STORE_PAGE_CONFIG (state, { skuPrefix, query, result }) {
    state.pageConfig = {
      ...state.pageConfig,
      [skuPrefix]: { query, result }
    }
  }
}

export const getters = {
  loaded: state => !!state.lastUpdateAll,
  
  products: state => state.products,

  product: state => (productId) => { return state.products[productId] },

  productBySkuPrefix: state => (skuPrefix) => {
    return Object.values(state.products).find(product => product.product.sku === skuPrefix) || {}
  },

  productLoadedLightweight: state => (skuPrefix) => {
    return !!Object.values(state.products).find(product => product.product.sku === skuPrefix)
  },

  bestsellers: (state, getters) => (skuPrefix, includeLE = false) => {
    let frameType = null
    if (skuPrefix.includes('-')) {
      [skuPrefix, frameType] = skuPrefix.split('-')
    }

    let bestsellers = (getters.productBySkuPrefix(skuPrefix).bestsellers || []).filter(
      bestseller => bestseller.in_stock
    ).map((bestseller) => {
      if (['vulcans', 'velans'].includes(skuPrefix)) {
        bestseller.skuFF = getters.changeFrameType(bestseller.sku, 'ff')
        bestseller.skuTF = getters.changeFrameType(bestseller.sku, 'tf')

        if (frameType) bestseller.sku = getters.changeFrameType(bestseller.sku, frameType)
      } else if (['airas'].includes(skuPrefix)) {
        bestseller.skuBF = getters.changeFrameType(bestseller.sku, 'bf')
        bestseller.skuZF = getters.changeFrameType(bestseller.sku, 'zf')

        if (frameType) bestseller.sku = getters.changeFrameType(bestseller.sku, frameType)
      } else if (['classics3', 'sierras', 'renegades'].includes(skuPrefix)) {
        bestseller.skuPol = bestseller.sku.replace('le_N', 'le_P')
        bestseller.skuNonPol = bestseller.sku.replace('le_P', 'le_N')
      }

      return bestseller
    })

    if (includeLE) {
      const limitedEditions = getters.limitedEditionsForSkuPrefix(skuPrefix).map(
        le => ({
          frame: null,
          frameStyling: null,
          in_stock: true,
          lens: null,
          lensStyling: null,
          sku: le.sku,
          isLE: true,
          limitedEditionPart: le.limitedEditionPart || null,
          buttonImg: le.buttonImg || null,
          title: le.title || null,
          swatchIconOverride: le.swatchIconOverride
        })
      ).filter(le => !le.sku.includes('fwt21')) // Hide FWT21 temporarily
      bestsellers = [...bestsellers.slice(0, 7 - limitedEditions.length), ...limitedEditions]
    }
    
    return bestsellers
  },

  changeFrameType: (state, getters) => (sku, newFrameType) => {
    const baseSku = getters.baseSKU(sku.split('-')[0])
    const design = Object.fromEntries(sku.split('-').slice(1).map(skuPart => skuPart.split('_')))

    // Determine what parts are changing
    let oldPart, newPart, framePart
    if (baseSku === 'vulcans') {
      oldPart = newFrameType === 'ff' ? 'vcn' : 'vcb'
      newPart = newFrameType === 'ff' ? 'vcb' : 'vcn'
      framePart = 'vcf'
    } else if (baseSku === 'velans') {
      oldPart = newFrameType === 'ff' ? 'ven' : 'veb'
      newPart = newFrameType === 'ff' ? 'veb' : 'ven'
      framePart = 'vef'
    } else if (baseSku === 'airas') {
      oldPart = newFrameType === 'bf' ? 'an' : 'ab'
      newPart = newFrameType === 'bf' ? 'ab' : 'an'
      framePart = 'af'
    }

    // Return early if newPart is already set
    if (newPart in design && !(oldPart in design)) return sku

    let newChoice = design[oldPart]
    if (['vcn', 'ven', 'an'].includes(newPart) && framePart in design) {
      newChoice = design[framePart]
    }
    
    design[newPart] = newChoice
    delete design[oldPart]

    let newSku = sku.split('-')[0]
    Object.entries(design).forEach(
      ([part, choice]) => { newSku += `-${part}_${choice}` }
    )
    return newSku
  },

  productDetails: (state, getters) => (skuPrefix) => {
    return getters.productBySkuPrefix(skuPrefix).product_details
  },

  bundle: (state, getters) => (skuPrefix) => {
    return (getters.productBySkuPrefix(skuPrefix) || {}).bundle_config
  },

  sortedProductDetails: (state, getters) => (skuPrefix) => {
    return getters.productBySkuPrefix(skuPrefix).product_details_sorted
  },
  
  baseSKU: state => (sku) => {
    const skuPrefix = (sku || '').split('-')[0]
    return state.baseSKUMapping[skuPrefix] || skuPrefix
  },

  pageMode: state => (sku) => { return state.pageModeMapping[sku] || 'custom' },

  spacer: state => (sku) => {
    if (sku === 'classics' || sku === 'classics2') sku = 'classics3'
    const imgAndOpacity = state.spacerMapping[sku] || []
    if (imgAndOpacity.length) return { img: imgAndOpacity[0], opacity: imgAndOpacity[1] }
    return { img: '1-rg_shadow.png', opacity: 0 }
  },

  productName: (state, getters) => (sku, html = true) => {
    if (!sku) return ''
    
    const skuPrefix = sku.split('-')[0]
    const baseSku = getters.baseSKU(skuPrefix)
    let product = getters.productBySkuPrefix(baseSku)
    if (!Object.keys(product).length) product = getters.productBySkuPrefix(skuPrefix) // For when we haven't loaded the base product yet
    let name = (product.product || {}).name || ''
    name = name.replace('Custom ', '')

    // Add FF/TF suffix
    if (['vulcans', 'velans'].includes(baseSku)) {
      if ((sku.includes('-tf') || sku.includes('vcn_') || sku.includes('ven_')) && !sku.includes('vcn_none') && !sku.includes('ven_none')) {
        name = name.replace(/(Vulcans™|Velans™)/i, '$1 TF')
      } else if ((sku.includes('-ff') || sku.includes('vcb_') || sku.includes('veb_')) && !sku.includes('vcb_none') && !sku.includes('veb_none')) {
        name = name.replace(/(Vulcans™|Velans™)/i, '$1 FF')
      }
    }

    // Add BF/ZF suffix
    if (['airas'].includes(baseSku)) {
      if ((sku.includes('-zf') || sku.includes('an_')) && !sku.includes('an_none')) {
        name = name.replace(/(Airas™)/i, '$1 ZF')
      } else if ((sku.includes('-bf') || sku.includes('ab_')) && !sku.includes('ab_none')) {
        name = name.replace(/(Airas™)/i, '$1 BF')
      }
    }

    if (!name.toLowerCase().includes('kit')) {
      if (skuPrefix.endsWith('ck')) {
        // Conversion Kit Suffix
        name += ' Conversion Kit'
      } else if (skuPrefix.endsWith('lrk')) {
        // Lens Replacement Kit Suffix
        name += ' Lens Kit'
      } else if (skuPrefix.endsWith('parts')) {
        const partKitType = getters.partKitType(sku)
        if (partKitType) name += ` ${partKitType[0].toUpperCase()}${partKitType.slice(1)} Kit`
      }
    }

    if (html) {
      name = name.replace('™', '<span class="tm">™</span>')
      name = name.replace('Conversion Kit', '<span class="kit">Conversion Kit</span>')
      name = name.replace('Lens Kit', '<span class="kit">Lens Kit</span>')
      name = name.replace('Frame Kit', '<span class="kit">Frame Kit</span>')
      name = name.replace('Strap Kit', '<span class="kit">Strap Kit</span>')
      name = name.replace('Ear Socks', '<span class="kit">Ear Socks Kit</span>')

      // McLaren must have the correct formatting
      name = name.replace('McLaren', '<span class="normal-case">Mc</span>Laren')
    }
    
    return name
  },

  limitedEditionProductName: (state, getters) => (sku) => {
    const productDetails = getters.productBySkuPrefix(sku)
    if (!Object.keys(productDetails).length) return ''

    return productDetails.product.name
  },

  limitedEditionPrice: (state, getters) => (sku) => {
    const productDetails = getters.productBySkuPrefix(sku)
    if (!Object.keys(productDetails).length) return ''

    return productDetails.product.price
  },

  partKitType: (state, getters) => (sku) => {
    const filteredParts = Object.entries(getters.detailsForSku(sku)).filter(
      ([partSku, partDetails]) => !partSku.includes('_none')
    )
    if (!filteredParts || !filteredParts.length) return null
    return filteredParts[0][1].part
  },

  swappableParts: state => baseSKU => state.swappableParts[baseSKU] || [],

  skuWithoutSwappableParts: (state, getters) => (sku) => {
    const baseSKU = getters.baseSKU(sku)
    const swappableParts = getters.swappableParts(baseSKU)
    if (!swappableParts.length) return sku
    return sku.replace(RegExp(swappableParts.join('|')), 'swappable')
  },

  stockManagedSku: state => (sku) => {
    sku = (sku || '').replace(/(?:ve)(n|b)_/, 's$1_') // Pace spherical
    sku = sku.replace(/(?:a)(n|b)_/, 'c$1_') // Airas
    return sku.replace(/([A-z0-9]{1,2}?)((i|s|es|cb|cn|sb|sn)_.+)/ig, '$2')
  },

  skuWithStockManagedParts: state => (sku) => {
    const stockManagedParts = {
      i: ['c3i', 'si', 'rgi', 'vci', 'vei', 'ai'],
      s: ['vs', 'sns'],
      es: ['vees', 'vces', 'aes'],
      cb: ['vcb', 'ab'],
      cn: ['vcn', 'an'],
      sb: ['veb'],
      sn: ['ven']
    }

    const skuParts = (sku || '').split('-')
    const additionalSkuParts = []
    skuParts.forEach((skuPart) => {
      const [part, choice] = skuPart.split('_')
      Object.entries(stockManagedParts).forEach(([stockManagedPart, dummyParts]) => {
        if (dummyParts.includes(part)) additionalSkuParts.push(`${stockManagedPart}_${choice}`)
      })
    })

    return [...skuParts, ...additionalSkuParts].join('-')
  },

  entryChoices: (state, getters, rootState) => (sku) => {
    /**
     * Determine entry sku
     *
     * Steps:
     * - Try to get SKU from URL
     * - Get SKU from defaults
     * - Strip out unwanted parts
     */
    const baseSKU = (sku || '').split('-')[0]
    const swappableParts = state.swappableParts[baseSKU] || []
    const productDetails = getters.productDetails(baseSKU) || {}
    const pageMode = getters.pageMode((sku || '').split('-')[0])

    const inStockOrPreorder = (skuPart) => {
      const stock = state.stock[getters.stockManagedSku(skuPart)] || { in_stock: true, preorder: false }
      return stock.in_stock || stock.preorder
    }

    // Get SKU from URL and determine frame type if its specified
    let unvalidatedEntrySKU = rootState.route.query.id || ''
    let frameType = null
    if (unvalidatedEntrySKU.endsWith('-ff') || unvalidatedEntrySKU.endsWith('-tf') || unvalidatedEntrySKU.endsWith('-bf') || unvalidatedEntrySKU.endsWith('-zf')) {
      frameType = unvalidatedEntrySKU.slice(-2)
      unvalidatedEntrySKU = unvalidatedEntrySKU.slice(0, -3)
    }

    // If no SKU has been defined then use a default SKU or bestseller
    const bestsellers = getters.bestsellers(baseSKU)
    if (unvalidatedEntrySKU === '' || unvalidatedEntrySKU === baseSKU) {
      if (bestsellers.length) {
        unvalidatedEntrySKU = bestsellers[0].sku

        // Enforce default frame type
        const defaultFrameTypeMapping = {
          vulcans: 'ff',
          velans: 'tf',
          airas: 'zf'
        }
        if (pageMode === 'custom' && baseSKU in defaultFrameTypeMapping) unvalidatedEntrySKU = getters.changeFrameType(unvalidatedEntrySKU, defaultFrameTypeMapping[baseSKU])
      }
    }

    // If no SKU has been defined there are no bestsellers in stock, try to build a SKU from scratch
    if (unvalidatedEntrySKU === '') unvalidatedEntrySKU = baseSKU

    // Handle TF and FF variants
    if (frameType !== null && swappableParts.length) {
      if (frameType === 'tf' || frameType === 'zf') unvalidatedEntrySKU = unvalidatedEntrySKU.replace(swappableParts[0], swappableParts[1])
      else if (frameType === 'ff' || frameType === 'bf') unvalidatedEntrySKU = unvalidatedEntrySKU.replace(swappableParts[1], swappableParts[0])
    }

    // Strip any parts that are unrecognised or aren't in stock
    let entryChoices = Object.fromEntries(unvalidatedEntrySKU.split('-').filter((skuPart) => {
      // Ignore parts with no _ in them
      if (!skuPart.includes('_')) return false

      const part = skuPart.split('_')[0]
      if (!(part in productDetails)) return false // Ignore parts that aren't in the config file
      
      const choices = productDetails[part].options || {}
      if (!(skuPart in choices)) return false // Ignore choices that aren't in the config file
      
      // Ignore choices that aren't in in stock but assume they are in stock if we haven't yet get a stock response
      if (!inStockOrPreorder(skuPart)) return false
      
      return true
    }).map(skuPart => skuPart.split('_')))

    // Add any missing parts to build a full SKU
    if (Object.keys(entryChoices).length === 0 || (pageMode !== 'parts' && pageMode !== 'conversionkit')) {
      Object.entries(productDetails).forEach(([part, details]) => {
        if (part === 'details') return // Ignore the details node
        if (part in entryChoices) return // Part is already defined
        
        // If this part is swappable and the alternate is already defined then continue
        if (swappableParts.includes(part) && swappableParts.filter(swappablePart => swappablePart !== part)[0] in entryChoices) return
  
        // Select the first in stock choice
        const inStockOptions = Object.entries(details.options).filter(([skuPart, _]) => inStockOrPreorder(skuPart))
        if (inStockOptions.length) entryChoices[part] = inStockOptions[0][0].split('_')[1]
      })
    }

    // Check only one part is defined for parts mode
    if ((pageMode === 'parts' || pageMode === 'conversionkit') && Object.keys(entryChoices).length > 1) {
      const firstKey = Object.keys(entryChoices)[0]
      entryChoices = { [firstKey]: entryChoices[firstKey] }
    }

    // Check no swappable parts are double-defined
    const definedSwappableParts = swappableParts.filter(swappablePart => swappablePart in entryChoices)
    if (definedSwappableParts.length > 1) definedSwappableParts.slice(1).forEach(part => delete entryChoices[part])

    // Ensure nose piece matches frame for pace series in custom mode
    if (pageMode === 'custom') {
      if ('vcf' in entryChoices && 'vcn' in entryChoices) entryChoices.vcn = entryChoices.vcf
      if ('vef' in entryChoices && 'ven' in entryChoices) entryChoices.ven = entryChoices.vef
      if ('af' in entryChoices && 'an' in entryChoices) entryChoices.an = entryChoices.af
    }

    return entryChoices
  },

  secondaryEntryChoices: (state, getters, rootState) => (sku, primaryDesign) => {
    const baseSKU = (sku || '').split('-')[0]
    const swappableParts = state.swappableParts[baseSKU] || []
    const productDetails = getters.productDetails(baseSKU)

    const inStockOrPreorder = (skuPart) => {
      const stock = state.stock[getters.stockManagedSku(skuPart)] || { in_stock: true, preorder: false }
      return stock.in_stock || stock.preorder
    }

    // Try to get SKU from URL
    const unvalidatedEntrySKU = rootState.route.query.ckid || ''
    if (!unvalidatedEntrySKU || !unvalidatedEntrySKU.includes('-')) return {} // Not defined, no CK enabled

    // Strip any parts that aren't valid or in stock
    const secondaryEntryChoices = unvalidatedEntrySKU.split('-').slice(1).filter((skuPart) => {
      // Strip any parts that don't include an underscore
      if (!skuPart.includes('_')) return false

      // Check part is in product_details and is in stock
      const part = skuPart.split('_')[0]
      if (!(part in productDetails)) return false // Ignore parts that aren't in the config file

      const options = productDetails[part].options
      const choices = Array.isArray(options) ? Object.fromEntries(options) : options
      if (!(skuPart in choices)) return false // Ignore choices that aren't in the config file
      
      // Ignore choices that aren't in in stock, assume in stock if we don't have any stock details for that part
      if (!inStockOrPreorder(skuPart)) return false

      // Check part isn't already defined in the primary design
      if (part in primaryDesign) return false

      return true
    }).map(skuPart => skuPart.split('_'))
    
    // Check no swappable parts are double-defined
    const definedSwappableParts = swappableParts.filter(swappablePart => swappablePart in secondaryEntryChoices)
    if (definedSwappableParts.length > 1) definedSwappableParts.slice(1).forEach(part => delete secondaryEntryChoices[part])

    return Object.fromEntries(secondaryEntryChoices)
  },

  allParts: state => state.allParts,
  
  allChoices: state => state.allChoices,

  choiceButtonStyling: (state, getters) => (choice) => {
    return (getters.allChoices[choice] || {}).styles || ''
  },

  detailsForSku: (state, getters) => (sku) => {
    if (!sku.includes('-')) return {}

    return Object.fromEntries(sku.split('-').slice(1).map((skuPart) => {
      const part = skuPart.split('_')[0]
      return [skuPart, {
        sku: skuPart,
        part_sku: part,
        ...getters.allChoices[skuPart],
        ...getters.allParts[part]
      } || null]
    }))
  },

  isInStock: (state, getters) => (sku) => {
    if (!sku) return false

    // Replace SKU with stock managed SKU for limited editions
    sku = (state.limitedEditionProductConfig[sku] || {}).stockManagedPart || sku

    const parts = sku.includes('-') && !(sku in state.stock) ? sku.split('-').slice(1) : [sku]

    return parts.map(
      part => getters.stockManagedSku(part)
    ).every(
      part => part in state.stock && state.stock[part].in_stock
    )
  },

  isOnPreorder: (state, getters) => (sku) => {
    if (!sku) return false

    // Replace SKU with stock managed SKU for limited editions
    sku = (state.limitedEditionProductConfig[sku] || {}).stockManagedPart || sku

    const parts = sku.includes('-') && !(sku in state.stock) ? sku.split('-').slice(1) : [sku]

    return parts.map(
      part => getters.stockManagedSku(part)
    ).some(
      part => part in state.stock && state.stock[part].preorder
    )
  },

  preorderDate: (state, getters) => (skus) => {
    if (!skus) return false

    if (typeof skus === 'string') skus = [skus]

    let timestamps = []
    skus.forEach((sku) => {
      if (!sku) return

      // Replace SKU with stock managed SKU for limited editions
      sku = (state.limitedEditionProductConfig[sku] || {}).stockManagedPart || sku
      
      const parts = sku.includes('-') && !(sku in state.stock) ? sku.split('-').slice(1) : [sku]

      timestamps = timestamps.concat(
        parts.map(
          part => getters.stockManagedSku(part)
        ).map(
          part => part in state.stock && state.stock[part].preorderDateUnix
        )
      )

      // Override preorderDate from RFU LE to the 5th May
      if (sku.includes('renegades-le_rfu')) timestamps.push(1620172800)
    })

    return timestamps.filter(a => a).reduce((a, b) => a > b ? a : b, -1)
  },

  delayedDespatchDate: (state, getters) => (skus) => {
    if (!skus) return false

    if (typeof skus === 'string') skus = [skus]

    let timestamps = []
    skus.forEach((sku) => {
      if (!sku) return

      // Replace SKU with stock managed SKU for limited editions
      sku = (state.limitedEditionProductConfig[sku] || {}).stockManagedPart || sku

      const parts = sku.includes('-') ? sku.split('-').slice(1) : [sku]

      timestamps = timestamps.concat(
        parts.map(
          part => getters.stockManagedSku(part)
        ).map(
          part => part in state.stock && state.stock[part].delayedShippingDate
        )
      )
    })

    return timestamps.filter(a => a).reduce((a, b) => a > b ? a : b, -1)
  },

  frameType: (state, getters) => (sku) => {
    if (!sku) return null
    const baseSKU = getters.baseSKU(sku)
    const parts = sku.split('-').slice(1).map(skuPart => skuPart.split('_')[0])
    
    if (['velans', 'vulcans'].includes(baseSKU)) {
      return (parts.includes('vcb') || parts.includes('veb')) ? 'ff' : 'tf'
    } else if (['airas'].includes(baseSKU)) {
      return (parts.includes('ab')) ? 'bf' : 'zf'
    }
    
    return null
  },

  getProductPartsForDisplay: (state, getters, rootState, rootGetters) => (item) => {
    const sku = item.sku || ''
    const skuPrefix = sku.split('-')[0]
    let details = getters.detailsForSku(sku)
    const frameType = getters.frameType(sku)
    const isCK = getters.pageMode(skuPrefix) === 'conversionkit'

    // Filter out unwanted parts
    details = Object.entries(details).filter(([skuPart, choiceDetails]) => {
      if (skuPart.endsWith('none')) return false // Don't show parts with no choice
      if (!choiceDetails.name) return false // Don't show parts with no name
      if (!isCK && ['tf', 'zf'].includes(frameType) && ['vcn', 'ven', 'an'].includes(choiceDetails.part_sku)) return false // Don't show nose piece for TF or ZF variants
      return true
    })

    // Sort parts by stockOrder node
    details = details.sort(([skuPartA, detailsA], [skuPartB, detailsB]) => (detailsA.stockOrder || 99) - (detailsB.stockOrder || 99))

    // Add gift cart details
    if (['egiftcard', 'pgiftcard'].includes(skuPrefix) && sku.includes('gs_shipping')) {
      details.push([null, {
        part: 'Value',
        name: new Intl.NumberFormat(rootGetters['store/locale'], {
          style: 'currency',
          currency: rootGetters['store/currency'],
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(item.gift_voucher_value || 0)
      }])

      details.push([null, {
        part: 'Shipping',
        name: (sku || '').includes('gs_shipping') ? 'Included' : 'Not Included'
      }])
    }

    // Reorder based on stockOrder key of config file
    return details.map(([skuPart, choiceDetails]) => choiceDetails)
  },

  caseDetails: () => (sku) => {
    const skuPrefix = sku.split('-')[0]
    if (skuPrefix !== 'case') return {}
    if (sku.includes('ct_capsule')) return { products: 'Classics³, Renegades™ and Sierras™', title: 'Lifestyle Series™' }
    if (sku.includes('ct_pacecapsule')) return { products: 'Vulcans™, Airas™ and Velans™', title: 'Pace Series™' }
    if (sku.includes('ct_revoltcapsule')) return { products: 'Snipers™ and Vanguards™', title: 'Snow Series™' }
    if (sku.includes('ct_compactvanguards')) return { products: 'Vanguards™', title: 'Vanguards™' }
    if (sku.includes('ct_compactsnipers')) return { products: 'Snipers™', title: 'Snipers™' }
    return {}
  },

  formatItemName: (state, getters) => (sku, name) => {
    name = (name || '').replace(/(Lens Kit|Conversion Kit|Part Kit)/i, '<span class="subtitle">$1<span>')
    const parts = getters.getProductPartsForDisplay(sku)
    const skuPrefix = sku.split('-')[0]

    if (['revoltparts', 'vanguardsparts', 'snipersparts'].includes(skuPrefix)) {
      if (parts.find(part => ['vf', 'rf', 'snf'].includes(part.part_sku))) name = name.replace('Part Kit', 'Frame Kit')
      else if (parts.find(part => ['vle', 'rle', 'snle'].includes(part.part_sku))) name = name.replace('Part Kit', 'Lens Kit')
      else if (parts.find(part => ['vs', 'rs', 's', 'sns'].includes(part.part_sku))) name = name.replace('Part Kit', 'Strap Kit')
    }

    if (skuPrefix === 'case') {
      const caseDetails = getters.caseDetails(sku)
      name += ` <span class="subtitle">${caseDetails.title || ''}</span>`

      if (sku.includes('ct_compact')) name = name.replace('Capsule Case', 'Compact Case')
    }

    // McLaren must have the correct formatting
    name = name.replace('McLaren', '<span class="normal-case">Mc</span>Laren')

    return name
  },

  priceFromSku: (state, getters) => (sku) => {
    const lePrice = getters.limitedEditionPrice(sku)
    if (lePrice) return lePrice
    
    let price = 0
    if (sku && sku.includes('-')) sku.split('-').slice(1).forEach((part) => { price += ((getters.allChoices[part] || {}).price || 0) })
    return price
  },

  minimumProductPrice: (state, getters) => (sku) => {
    const lensTech = getters.lensTech(sku)
    const lensPart = sku.split('-').slice(1).map(part => part.split('_')[0]).find(part => part.match(/.+le$/))
    const lensPrices = getters.lensTechPrices(lensPart)
    const priceWithoutLenses = getters.priceFromSku(sku) - lensPrices[lensTech]
    return priceWithoutLenses + Math.min(...Object.values(lensPrices))
  },

  minimumPartPrice: (state, getters) => (partPrefix) => {
    return (((getters.allParts[partPrefix] || {}).options || []).map(
      ([_, details]) => details
    ).sort(
      (a, b) => a.price - b.price
    )[0] || {}).price
  },

  limitedEditions: (state, getters, rootState, rootGetters) => {
    const isUnhiddenByPromo = sku => !!rootGetters['promo/activePromoForProduct'](sku)
    
    let counter = Object.entries(state.limitedEditionIds).length
    const productLEs = Object.entries(state.limitedEditionIds).map(
      ([sku, id]) => {
        const details = {
          sku,
          offset: counter,
          ...getters.product(id),
          ...getters.limitedEditionProductConfig(sku)
        }
        counter -= 1
        return details
      }
    ).filter(
      details => !details.sku.startsWith('mavericks') &&
        !['le_gold', 'le_liftoff'].includes(details.sku) &&
        details.product &&
        (!details.hidden || isUnhiddenByPromo(details.sku))
    )

    const partLEs = Object.entries(getters.limitedEditionParts).filter(([sku, part]) => !part.isSignatureSeries).map(
      ([sku, details]) => {
        const product = getters.allChoices[sku] || {}
        details = {
          isLEPart: true,
          is_saleable: product.stock,
          product,
          name: `${details.title} Limited Edition ${getters.productName(details.sku.split('-')[0], false)}`,
          ...details
        }
        return details
      }
    )
    
    const sortedLEs = [
      ...productLEs,
      ...partLEs
    ].sort((a, b) => b.offset - a.offset)

    return sortedLEs
  },

  signatureSeries: (state, getters) => {
    let counter = Object.entries(state.signatureSeriesIds).length
    const productLEs = Object.entries(state.signatureSeriesIds).map(
      ([sku, id]) => {
        const details = {
          sku,
          offset: counter,
          ...getters.product(id),
          ...getters.limitedEditionProductConfig(sku)
        }
        counter -= 1
        return details
      }
    ).filter(details => !details.hidden)

    const partLEs = Object.entries(getters.limitedEditionParts).filter(([sku, part]) => part.isSignatureSeries).map(
      ([sku, details]) => {
        const product = getters.allChoices[sku] || {}
        details = {
          isLEPart: true,
          is_saleable: product.stock,
          product,
          name: `${details.title} Signature Series ${getters.productName(details.sku.split('-')[0], false)}`,
          ...details
        }
        return details
      }
    )

    const sortedLEs = [
      ...productLEs,
      ...partLEs
    ].sort((a, b) => b.offset - a.offset)

    return sortedLEs
  },

  limitedEditionsForSkuPrefix: (state, getters, rootState, rootGetters) => (skuPrefix) => {
    const isUnhiddenByPromo = sku => !!rootGetters['promo/activePromoForProduct'](sku)

    const bestsellers = Object.values(getters.products).map(
      product => ({ ...product, ...getters.limitedEditionProductConfig(product.sku) })
    ).filter(
      product => product.sku.includes('-le_') &&
        product.is_saleable &&
        product.sku.startsWith(skuPrefix) &&
        getters.isInStock(product.sku) &&
        (!product.hidden || isUnhiddenByPromo(product.sku))
    )

    Object.entries(getters.limitedEditionParts).filter(
      ([_, leDetails]) => leDetails.sku.startsWith(`${skuPrefix}-`) && getters.isInStock(leDetails.sku)
    ).forEach(
      ([limitedEditionPart, leDetails]) => {
        bestsellers.push({
          ...leDetails,
          isLE: true,
          limitedEditionPart
        })
      }
    )

    return bestsellers
  },

  productImages: (state, getters) => (sku) => {
    // Get product images which match the base product
    const baseSKU = getters.baseSKU(sku)
    const baseProductImages = state.productImages.filter(image => image.product === baseSKU)
    if (!baseProductImages.length) return {}

    // Determine parts to match based on a pre-defined priority (logic matched serverside)
    const parts = sku.split('-').slice(1).map((skuPart) => {
      const part = skuPart.split('_')[0]
      const partName = (getters.allParts[part] || {}).part
      const priority = {
        icons: 5,
        icon: 5,
        frames: 10,
        frame: 10,
        bottom: 10,
        'top frame': 10,
        lenses: 20,
        lens: 20,
        straps: 15,
        strap: 15,
        'ear socks': 5
      }[(partName || '').toLowerCase()] || 0
      return { skuPart, priority }
    }).sort(
      (a, b) => b.priority - a.priority
    ).slice(0, 2).map(part => part.skuPart)

    // Try to find a match looking at both part_1 and part_2
    const currentLensTech = getters.lensTech(sku)
    const partsMatch = (part1, part2) => getters.changeLensTech(part1, currentLensTech) === getters.changeLensTech(part2, currentLensTech)
    let search = {}
    if (parts.length === 2) {
      search = baseProductImages.find(image => partsMatch(image.part_1, parts[0]) && partsMatch(image.part_2, parts[1]))
      if (search) return search
    }

    // Try to just match part_1
    if (parts.length >= 1) {
      search = baseProductImages.find(image => partsMatch(image.part_1, parts[0]))
      if (search) return search
    }

    // Failover to just returning the first baseProductImage
    return baseProductImages[0]
  },

  normaliseSKUForFacebook: state => (sku) => {
    const invalidParts = ['c2e', 'c3e', 'rge', 'pe', 'se', 're', 've', 'me', 'i', 's', 'e', 'lre', 'po', 'ge', 'vce', 'vee', 'es']
    sku = (sku || '').split('-').filter(part => !invalidParts.some(validPart => part.startsWith(`${validPart}_`))).join('-')
    sku = sku.replace(/[A-z3]{1,2}(i|es)_[A-z0-9]+/g, '').replace(/-+/g, '-').replace(/-$/g, '')
    return sku
  },

  extraSKUs: getters => (design, skuPrefix) => {
    let partsMode = null
    if (!['revoltparts', 'vanguardsparts', 'snipersparts'].includes(skuPrefix)) return []
    if ('rf' in design || 'vf' in design || 'snf' in design) partsMode = 'frame'
    if ('rle' in design || 'vle' in design || 'snle' in design) partsMode = 'lens'
    if ('rs' in design || 'vs' in design || 'sns' in design) partsMode = 'strap'

    const mapping = {
      vanguardsparts: {
        frame: ['ge_box', 'ge_outerbox', 'ge_microfiber', 've_booklet', 'e_stickers'],
        lens: ['ge_lensbox', 'ge_outerlensbox', 'ge_microfiber', 've_booklet', 'e_stickers'],
        strap: ['ve_booklet', 'e_stickers']
      },
      revoltparts: {
        frame: ['ge_box', 'ge_outerbox', 'ge_microfiber', 're_booklet', 'e_stickers'],
        lens: ['ge_lensbox', 'ge_outerlensbox', 'ge_microfiber', 're_booklet', 'e_stickers'],
        strap: ['re_booklet', 'e_stickers']
      },
      snipersparts: {
        frame: ['ge_box', 'ge_outerbox', 'ge_microfiber', 'sne_booklet', 'e_stickers'],
        lens: ['ge_lensbox', 'ge_outerlensbox', 'ge_microfiber', 'sne_booklet', 'e_stickers'],
        strap: ['sne_booklet', 'e_stickers']
      }
    }

    if (!(skuPrefix in mapping)) return []
    return mapping[skuPrefix][partsMode] || []
  },

  limitedEditionParts: state => Object.fromEntries(Object.entries(state.limitedEditionParts).map(
    ([lePart, lePartDetails]) => [lePart, { ...lePartDetails, lePart }]
  )),

  icon: state => sku => state.iconMapping[sku] || 'sgi-renegades',

  baseSkuFromPart: (state, getters) => (part) => {
    // Takes a part (eg 'c3le') and returns the base SKU for that part
    const baseSkus = [...new Set(Object.values(state.baseSKUMapping))]
    return baseSkus.find((baseSku) => {
      const product = getters.productBySkuPrefix(baseSku)
      if (product.product_details) return part in product.product_details
      return false
    })
  },

  lensTech: (state, getters) => (sku) => {
    // Takes a SKU and returns the lens technology of that product. Defaults to 4ko
    // 4KO: *le_N*
    // 4KO Pol: *le_P*
    // 8KO: *le_8(!P)* or *le_8N*
    // 8KO Pol: *le_8P*
    // 8KO Iris: *le_8IR*
    // 4KOsnow: *le_*N*

    // Checks Limited Editions first
    const limitedEditionLensTech = getters.limitedEditionProductConfig(sku).lensTechnology
    if (limitedEditionLensTech) return limitedEditionLensTech

    const lookup = [
      [/le_N/g, '4ko'],
      [/le_P/g, '4kop'],
      [/le_8(?!P|IR)/g, '8ko'],
      [/le_8P/g, '8kop'],
      [/le_8IR/g, 'iris'],
      [/le_[a-z]{3,10}N/g, '4kosnow']
    ]

    return (lookup.find(
      ([pattern, tech]) => (sku || '').match(pattern)
    ) || lookup[0])[1]
  },

  changeLensTech: (_, getters) => (sku, targetLensTech) => {
    if (!sku) return

    const currentTech = getters.lensTech(sku)

    const prefixLookup = {
      '4ko': 'N',
      '4kop': 'P',
      '8ko': '8',
      '8kop': '8P'
    }
    
    if (!(currentTech in prefixLookup) || !(targetLensTech in prefixLookup)) return sku

    return sku.replace(`le_${prefixLookup[currentTech]}`, `le_${prefixLookup[targetLensTech]}`)
  },

  lensTechPrices: (_, getters) => (partPrefix) => {
    const lensOptions = ((getters.allParts[partPrefix] || {}).options || []).map(
      ([sku, details]) => ({ ...details, sku })
    )

    const lensTechPrices = {}
    lensOptions.forEach((option) => {
      const lensTech = getters.lensTech(option.sku)
      lensTechPrices[lensTech] = option.price
    })

    return lensTechPrices
  },

  limitedEditionProductConfig: state => sku => state.limitedEditionProductConfig[sku] || {},

  pageConfig: state => skuPrefix => state.pageConfig[skuPrefix] || {}
}

export const actions = {
  async GET_ALL_PRODUCT_DETAILS ({ commit, state, rootGetters }) {
    logger.debug('GET_ALL_PRODUCT_DETAILS')

    const { data } = await this.$publicAPI.quietGet(`products/?c=${rootGetters['store/store']}`, { useCache: true, timeout: 20000 })

    commit('BATCH_SET_PRODUCT_DETAILS', { products: data.products })
    commit('SET_ALL_CHOICES')
    commit('SET_ALL_PARTS')

    commit('SET_LIMITED_EDITION_IDS', data.limited_editions)
    commit('SET_SIGNATURE_SERIES_IDS', data.signature_series)

    logger.debug('GET_ALL_PRODUCT_DETAILS succeeded')
    return true
  },

  async GET_LE_PRODUCT_CONFIG ({ commit }) {
    logger.debug('GET_LE_PRODUCT_CONFIG')

    const result = (await this.$contentAPI.fetch({ query: limitedEditionProductsQuery }) || {}).allProducts || []
    commit('SET_LE_PRODUCT_CONFIG', result)
    return result
  },

  async GET_PDP_MAPPING ({ commit }) {
    logger.debug('GET_PDP_MAPPING')

    const response = await this.$contentAPI.fetch({ query: pdpMappingQuery })
    const result = (response || {}).allBaseProducts || []
    commit('SET_PDP_MAPPING', result)
    return result
  },

  async GET_ALL_PRODUCT_IMAGES ({ commit, state }) {
    const { data } = await this.$publicAPI.quietGet('insta/all_product_images/')
    commit('SET_PRODUCT_IMAGES', data)
    return data
  },

  async GET_ALL_STOCK ({ state, commit }, force = false) {
    logger.debug('GET_ALL_STOCK')

    try {
      const { data } = await this.$publicAPI.quietGet('products/stock/')
      commit('SET_STOCK', data)

      logger.debug('GET_ALL_STOCK succeeded')
      return true
    } catch (error) {
      logger.debug('GET_ALL_STOCK failed')
      logger.error(error)
      this.app.$sentry.captureException(error)
      return false
    }
  }
}
